// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows outside swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 */

/* eslint-disable spire/fenced-imports */
import SwiperCore, { A11y, Autoplay, Controller, Navigation, Pagination, Scrollbar } from "swiper";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";
/* eslint-enable spire/fenced-imports */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import ArrowNavigation from "@tps/App/Components/XngCarousel/ArrowNavigation";
import { xSwiperStyle } from "@tps/App/Components/XngCarousel/styles";
import { color, xScreen } from "@tps/XDL/Utilities";
import { mergeWithCss } from "@tps/XDL/Utilities/mergeWithCss";
import pick from "lodash/pick";
import React, { FC, isValidElement, useEffect, useState } from "react";
import { css } from "styled-components";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay]);

export type Props = SwiperProps & {
    extendedStyles?: SwiperStyle;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

// project style
const swiperStyle: SwiperStyle = {
    container: {
        css: css`
            ${xSwiperStyle}

            .xng-pagination-container {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-top: 30px;
                gap: 16px;

                & > div,
                & > button {
                    margin: 0;
                }
            }

            .swiper-scrollbar.swiper-scrollbar-horizontal {
                top: unset;
                right: unset;
                transform: initial;
                width: calc(100% - 96px);
                height: 8px;
                left: 48px;
                bottom: 12px;
                border-radius: 1px;
                border-inline: 2px solid ${color("common.ui2")};
                background: ${color("common.ui2")};
                ${xScreen("lte", "md")} {
                    border-inline: 2px solid ${color("common.ui3")};
                    background: ${color("common.ui3")};
                }
                overflow: hidden;
                & > .swiper-scrollbar-drag {
                    background: ${color("common.ui5")};
                    height: 4px;
                    top: 2px;
                    border-radius: 0;
                }

                &.swiper-scrollbar-lock + div {
                    display: none !important;
                }
            }
            .swiper-pagination-bullets.swiper-pagination-horizontal {
                bottom: -20px !important;
            }
            .swiper-pagination {
                position: relative;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;
                &.swiper-pagination-lock {
                    display: none;
                }

                .swiper-pagination-bullet {
                    width: 10px;
                    height: 10px;
                    background: ${color("common.white", 0)};
                    border: 1px solid ${color("common.white")};
                    border-radius: 100%;
                    opacity: 1;
                    margin: 0 2px !important;
                    &:hover:not(.swiper-pagination-bullet-active) {
                        background: ${color("common.white")};
                        border-color: ${color("common.white")};
                    }
                    &.swiper-pagination-bullet-active {
                        width: 10px;
                        height: 10px;
                        background: ${color("common.white")};
                        border: 1px solid ${color("common.white")};
                    }
                }
            }
        `,
    },
};

const XngCarousel: FC<Props> = props => {
    const { children, className, extendedStyles, ...swiperProps } = props;
    const carouselContainerRef = React.createRef<HTMLDivElement>();

    const [isSwiperCssImported, setIsSwiperCssImported] = useState(false);
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));
    const [paginationWidth, setPaginationWidth] = useState(0);

    useEffect(() => {
        const getStyle = async () => {
            /* eslint-disable global-require */
            await require("swiper/swiper-bundle.css");
            /* eslint-enable global-require */
        };
        getStyle().then(_ => setIsSwiperCssImported(true));
    }, []);

    useEffect(() => {
        if (carouselContainerRef.current && swiperProps.pagination) {
            const paginationBulletsCount = carouselContainerRef.current.querySelectorAll(".swiper-pagination-bullet")?.length || 0;
            // (width of arrows) + (width of bullet *Count) + (width of bullet margin *Count) + (margin of arrows)
            // 14*2              + 10*Count                 + 4*Count                         + 22*2
            const calculatedWidth = 14 * paginationBulletsCount + 72;
            if (paginationWidth !== calculatedWidth) {
                setPaginationWidth(calculatedWidth);
            }
        }
    }, [carouselContainerRef])

    if (!isSwiperCssImported) {
        return null;
    }

    if (props.scrollbar === true) {
        swiperProps.scrollbar = XngCarousel.defaultProps?.scrollbar;
        // to fix the issue of .swiper-scrollbar-drag not giving a width after showing/hiding container slides
        swiperProps.observeParents = true;
        swiperProps.observer = true;
    }

    return (
        <StyledWrapper {...styles.container} className={`carousel-main-wrapper ${className || ""}`} ref={carouselContainerRef}>
            <Swiper {...swiperProps}>
                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props, [...swiperSlidePropsKeys]);
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                        </SwiperSlide>
                    );
                })}

                {(swiperProps.pagination || swiperProps.navigation || swiperProps.scrollbar) && (
                    <StyledWrapper
                        style={{
                            display: "flex",
                            alignItems: "center",
                            maxWidth: swiperProps.scrollbar ? "100%" : `${paginationWidth}px`,
                            width: "100%",
                            position: "relative",
                            margin: swiperProps.scrollbar ? "20px auto 0" : "0 auto",
                            justifyContent: "space-between",
                        }}
                    >
                        {swiperProps.navigation && <ArrowNavigation arrowType="prev" showOnMobile={true} withScrollbar={!!swiperProps.scrollbar} />}
                        <StyledWrapper className="xng-pagination-container">
                            {swiperProps.pagination && <div className="swiper-pagination" />}
                            {swiperProps.pagination && <div className="swiper-pagination-bullets" />}
                            {swiperProps.scrollbar && <div className="swiper-scrollbar" />}
                        </StyledWrapper>
                        {swiperProps.navigation && <ArrowNavigation arrowType="next" showOnMobile={true} withScrollbar={!!swiperProps.scrollbar} />}
                    </StyledWrapper>
                )}
            </Swiper>
        </StyledWrapper>
    );
};

XngCarousel.defaultProps = {
    slidesPerView: "auto",
    navigation: true,
    simulateTouch: true,
    spaceBetween: 20,
    pagination: false,
    scrollbar: { draggable: true, snapOnRelease: true },
    watchOverflow: true,
    updateOnWindowResize: true,
    centerInsufficientSlides: true,
    draggable: false,
    freeMode: false,
};

export default XngCarousel;
